import React, { useEffect, useState, useMemo } from 'react';
import type { AssetClassVisibility } from '../../lib/assetClassVisibility';
import {
  Check,
  Circle,
  Info,
  RotateCcw,
  Layout,
  X
} from 'lucide-react';

// --- CANONICAL DATA ---
const CANONICAL_COLUMNS = [
  { id: 'tradeId', label: 'TradeId', category: 'TRADE INFORMATION' },
  { id: 'opened', label: 'Opened', category: 'TIME & PRICE' },
  { id: 'symbol', label: 'Symbol', category: 'TRADE INFORMATION' },
  { id: 'side', label: 'Side', category: 'TRADE INFORMATION' },
  { id: 'volume', label: 'Max Size', category: 'TRADE INFORMATION' },
  { id: 'executions', label: 'Executions', category: 'TRADE INFORMATION' },
  { id: 'entryPrice', label: 'Entry Price', category: 'TIME & PRICE' },
  { id: 'exitPrice', label: 'Exit Price', category: 'TIME & PRICE' },
  { id: 'totalFees', label: 'Total Fees', category: 'RISK ANALYSIS', hasInfo: true },
  { id: 'pnl', label: 'Profit & Loss', category: 'PERFORMANCE METRICS' },
  { id: 'duration', label: 'Duration', category: 'TIME & PRICE' },
  { id: 'pnlR', label: 'Profit & Loss (R)', category: 'PERFORMANCE METRICS' },
  { id: 'pnlTicksGrossFutures', label: 'P&L (Ticks) · Futures Only', category: 'PERFORMANCE METRICS', assetClass: 'FUTURES' },
  { id: 'initialRisk', label: 'Initial Risk', category: 'RISK ANALYSIS' },
  { id: 'positionMAE', label: 'Position MAE', category: 'RISK ANALYSIS' },
  { id: 'positionMFE', label: 'Position MFE', category: 'RISK ANALYSIS' },
  { id: 'closed', label: 'Closed', category: 'TIME & PRICE' },
  { id: 'notes', label: 'Notes', category: 'TRADE INFORMATION' },
  { id: 'tags', label: 'Tags', category: 'TRADE INFORMATION' },
];

const PRESETS = {
  Default: new Set(['tradeId', 'opened', 'symbol', 'side', 'entryPrice', 'exitPrice', 'totalFees', 'pnl', 'duration', 'notes', 'tags']),
  Advanced: new Set(CANONICAL_COLUMNS.map(c => c.id)),
  'Futures-Focused': new Set(['tradeId', 'opened', 'symbol', 'side', 'volume', 'executions', 'entryPrice', 'exitPrice', 'pnl', 'pnlR', 'pnlTicksGrossFutures', 'initialRisk', 'positionMAE', 'positionMFE', 'duration', 'totalFees', 'tags'])
};

type PresetName = 'Default' | 'Advanced' | 'Futures-Focused';

// --- UTILITIES ---
const areSetsEqual = (a: Set<string>, b: Set<string>) => a.size === b.size && [...a].every(value => b.has(value));

interface TableColumnsManagerProps {
  savedColumns: Record<string, boolean> | null;
  onSave: (columns: Record<string, boolean>, presetIntent?: PresetName | null) => boolean;
  onCancel: () => void;
  initialPreset?: PresetName | null;
  validateSelection?: (columns: Record<string, boolean>) => { fits: boolean; message?: string | null };
  autoHideSelection?: (columns: Record<string, boolean>, presetIntent?: PresetName | null) => { columns: Record<string, boolean>; hiddenCount: number; fits: boolean };
  assetClassVisibility?: AssetClassVisibility;
}

const TableColumnsManager: React.FC<TableColumnsManagerProps> = ({ savedColumns, onSave, onCancel, initialPreset = null, validateSelection, autoHideSelection, assetClassVisibility }) => {
  const futuresEnabled = assetClassVisibility?.FUTURES !== false;
  const isColumnSupported = (column: typeof CANONICAL_COLUMNS[number]) => {
    if (column.assetClass === 'FUTURES') return futuresEnabled;
    return true;
  };
  const visibleColumns = useMemo(() => CANONICAL_COLUMNS.filter(isColumnSupported), [futuresEnabled]);
  const visibleColumnIds = useMemo(() => new Set(visibleColumns.map((column) => column.id)), [visibleColumns]);
  const visiblePresets = useMemo(
    () => (futuresEnabled ? ['Default', 'Advanced', 'Futures-Focused', 'Custom'] : ['Default', 'Advanced', 'Custom']),
    [futuresEnabled],
  );
  const visiblePresetColumnSet = (name: PresetName) => {
    return new Set([...PRESETS[name]].filter((id) => visibleColumnIds.has(id)));
  };
  // Convert from Record<string, boolean> to Set of enabled column ids
  const savedSet = useMemo(() => {
    if (initialPreset && PRESETS[initialPreset as keyof typeof PRESETS]) {
      return visiblePresetColumnSet(initialPreset);
    }
    const set = new Set<string>();
    if (savedColumns) {
      Object.entries(savedColumns).forEach(([key, enabled]) => {
        if (enabled) {
          // Map settings keys back to column ids
          const col = CANONICAL_COLUMNS.find(c => c.id === key);
          if (col) set.add(col.id);
        }
      });
    }
    return set;
  }, [savedColumns, initialPreset, visibleColumnIds]);

  // 1. DRAFT STATE: Initialized from saved state
  const [draftSet, setDraftSet] = useState(new Set(savedSet));

  useEffect(() => {
    setDraftSet(new Set(savedSet));
  }, [savedSet]);

  // 2. OPENING SNAPSHOT: For Reset functionality
  const openingSnapshot = useMemo(() => new Set(savedSet), [savedSet]);

  // 3. DERIVED PRESET KIND
  const activePresetKind = useMemo(() => {
    if (areSetsEqual(draftSet, visiblePresetColumnSet('Default'))) return 'Default';
    if (areSetsEqual(draftSet, visiblePresetColumnSet('Advanced'))) return 'Advanced';
    if (futuresEnabled && areSetsEqual(draftSet, visiblePresetColumnSet('Futures-Focused'))) return 'Futures-Focused';
    return 'Custom';
  }, [draftSet, futuresEnabled, visibleColumnIds]);

  // 4. INTERACTION HANDLERS
  const handleToggleChip = (id: string) => {
    const next = new Set(draftSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setDraftSet(next);
  };

  const handleApplyPreset = (name: string) => {
    if (PRESETS[name as keyof typeof PRESETS]) setDraftSet(visiblePresetColumnSet(name as PresetName));
  };

  const handleReset = () => {
    setDraftSet(new Set(openingSnapshot));
  };

  const groupedColumns = useMemo(() => {
    return visibleColumns.reduce((acc, col) => {
      if (!acc[col.category]) acc[col.category] = [];
      acc[col.category].push(col);
      return acc;
    }, {} as Record<string, typeof CANONICAL_COLUMNS>);
  }, [visibleColumns]);

  const draftSelection = useMemo(() => {
    const result: Record<string, boolean> = {};
    CANONICAL_COLUMNS.forEach(col => {
      result[col.id] = visibleColumnIds.has(col.id) ? draftSet.has(col.id) : Boolean(savedColumns?.[col.id]);
    });
    return result;
  }, [draftSet, savedColumns, visibleColumnIds]);

  const fitState = useMemo(() => {
    if (!validateSelection) return { fits: true, message: null as string | null };
    const result = validateSelection(draftSelection);
    return {
      fits: result.fits,
      message: result.message ?? null,
    };
  }, [draftSelection, validateSelection]);

  const handleAutoHideWeakest = () => {
    if (!autoHideSelection) return;
    const presetIntent = activePresetKind === 'Default' || activePresetKind === 'Advanced' || activePresetKind === 'Futures-Focused'
      ? (activePresetKind as PresetName)
      : null;
    const result = autoHideSelection(draftSelection, presetIntent);
    const next = new Set<string>();
    Object.entries(result.columns).forEach(([key, enabled]) => {
      if (enabled) next.add(key);
    });
    setDraftSet(next);
  };

  const handleSave = () => {
    if (!fitState.fits) return;
    const presetIntent = activePresetKind === 'Default' || activePresetKind === 'Advanced' || activePresetKind === 'Futures-Focused'
      ? (activePresetKind as PresetName)
      : null;
    const success = onSave(draftSelection, presetIntent);
    if (success) {
      onCancel();
    }
  };

  const modalContent = (
    <div className="pointer-events-auto flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-border bg-white shadow-2xl dark:bg-[#0f0f12]" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="relative flex items-start justify-between p-6 pb-4">
          <div className="flex gap-4">
            <div className="h-12 w-12 shrink-0 rounded-[4px] border border-zinc-200 bg-zinc-50 p-0.5 dark:border-zinc-700/50 dark:bg-zinc-800/50">
              <Layout strokeWidth={1} className="h-full w-full text-zinc-500 dark:text-zinc-400" />
            </div>
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-zinc-900 dark:text-white">Table Columns</h1>
              <p className="text-sm text-zinc-500 dark:text-zinc-500">Select which columns appear in your trades table</p>
            </div>
          </div>
          <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCancel(); }} className="p-1 text-zinc-500 transition-colors hover:text-zinc-900 dark:hover:text-white">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Preset Row with Frosted Effect */}
        <div className="px-6 pb-6 relative group">
          <div className="pointer-events-none absolute inset-x-6 inset-y-0 rounded-2xl bg-blue-500/10 opacity-25 blur-xl transition-opacity group-hover:opacity-40 dark:opacity-40 dark:group-hover:opacity-60" />

          <div
            className="relative z-10 grid gap-1.5 rounded-xl border border-border bg-zinc-50 p-1 shadow-[inset_0_2px_6px_rgba(15,23,42,0.14)] dark:bg-zinc-900/40 dark:shadow-[inset_0_2px_8px_rgba(0,0,0,0.65)] dark:backdrop-blur-md"
            style={{ gridTemplateColumns: `repeat(${visiblePresets.length}, minmax(0, 1fr))` }}
          >
            {visiblePresets.map((name) => (
              <button
                key={name}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (name !== 'Custom') handleApplyPreset(name);
                }}
                disabled={name === 'Custom'}
                className={`pointer-events-auto py-2.5 px-3 text-sm font-medium rounded-lg transition-all duration-300 relative overflow-hidden ${
                  activePresetKind === name
                    ? 'text-white shadow-[0_6px_14px_rgba(37,99,235,0.42)]'
                    : 'text-zinc-500 hover:bg-white hover:text-zinc-700 disabled:cursor-default dark:hover:bg-white/5 dark:hover:text-zinc-300'
                }`}
              >
                {activePresetKind === name && (
                  <div className="pointer-events-none absolute inset-0 bg-blue-600 z-0" />
                )}
                <span className="relative z-10">{name}</span>
              </button>
            ))}
          </div>

          {!fitState.fits ? (
            <div className="relative z-10 mt-3 flex items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/50 dark:bg-amber-950/30 dark:text-amber-200">
              <p className="flex items-center gap-2 leading-5 text-amber-800/95 dark:text-amber-200/95">
                <Info className="h-4 w-4 shrink-0 text-amber-500 dark:text-amber-400" />
                <span>
                  {fitState.message ?? (activePresetKind === 'Futures-Focused'
                    ? 'Futures-Focused exceeds the current table width.'
                    : 'This column layout exceeds the current table width.')}
                </span>
              </p>
              {autoHideSelection ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAutoHideWeakest();
                  }}
                  className="shrink-0 rounded-lg border border-amber-300 bg-white px-3 py-2 text-xs font-semibold text-amber-800 transition-colors hover:bg-amber-100 dark:border-amber-400/40 dark:bg-amber-500/10 dark:text-amber-100 dark:hover:bg-amber-500/20"
                >
                  Auto-hide weakest columns
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Main Selection Area */}
        <div className="flex-1 overflow-y-auto px-6 space-y-8 custom-scrollbar pb-8">
          {Object.entries(groupedColumns).map(([category, columns]) => (
            <section key={category}>
              <div className="flex items-center gap-4 mb-4">
                <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                  {category}
                </h2>
                <div className="h-px flex-1 bg-border" />
              </div>
              <div className="flex flex-wrap gap-2">
                {columns.map((col) => {
                  const isActive = draftSet.has(col.id);
                  return (
                    <button
                      key={col.id}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleToggleChip(col.id);
                      }}
                      className={`pointer-events-auto flex items-center gap-2 px-3.5 py-2 rounded-lg border text-[13px] transition-all duration-200 ${
                        isActive
                          ? 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-500/40 dark:bg-blue-600/10 dark:text-blue-50'
                          : 'border-zinc-200 bg-zinc-50 text-zinc-500 hover:border-zinc-300 hover:text-zinc-700 dark:border-zinc-800/80 dark:bg-zinc-900/30 dark:hover:border-zinc-600 dark:hover:text-zinc-400'
                      }`}
                    >
                      {isActive ? (
                        <Check className="w-3.5 h-3.5" />
                      ) : (
                        <Circle className="w-3.5 h-3.5 opacity-30" />
                      )}
                      <span className="font-medium">{col.label}</span>
                      {col.hasInfo && (
                        <Info className="w-3.5 h-3.5 opacity-50" />
                      )}
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border bg-zinc-50 p-4 dark:bg-[#0c0c0e]">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleReset();
            }}
            className="group flex items-center gap-2 rounded-lg border-[0.8px] border-zinc-300 bg-transparent px-4 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-100 dark:border-zinc-700 dark:bg-transparent dark:text-zinc-200 dark:hover:bg-zinc-800/70"
          >
            <RotateCcw className="w-4 h-4 group-active:rotate-[-90deg] transition-transform" />
            Reset changes
          </button>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onCancel();
              }}
              className="rounded-lg border-[0.8px] border-zinc-300 bg-white px-5 py-2 text-sm font-semibold text-zinc-700 transition-colors hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!fitState.fits}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSave();
              }}
              className={`px-7 py-2.5 text-sm font-semibold rounded-lg transition-all ${
                fitState.fits
                  ? 'text-white bg-blue-600 hover:bg-blue-500 active:scale-95 shadow-[0_4px_20px_rgba(37,99,235,0.25)] shadow-blue-600/20'
                  : 'cursor-not-allowed bg-zinc-200 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-500'
              }`}
            >
              {activePresetKind === 'Custom' ? 'Save Custom' : 'Save Layout'}
            </button>
          </div>
        </div>
      </div>
  );

  return (
    <div
      className="fixed inset-0 z-[2147483647] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      aria-modal="true"
      role="dialog"
      onMouseDown={(e) => {
        if (e.target !== e.currentTarget) return;
        onCancel();
      }}
    >
      {modalContent}

      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar { width: 5px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #27272a; border-radius: 10px; }
      `}} />
    </div>
  );
};

export default TableColumnsManager;
